// src/components/draft/PlayerSearchWithManual.js - Player Search with Manual Entry for Draft
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, User, X, AlertCircle, Plus, AlertTriangle } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import PlayerLink from '../PlayerLink';

const PlayerSearchWithManual = ({
  leagueId,
  onPlayerSelect,
  onMakePick,
  onMakePhantomPick,
  selectedPlayer,
  isPickingPlayer,
  draftedPlayerIds = new Set()
}) => {
  // ========================================
  // STATE
  // ========================================
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  
  // Manual entry fields
  const [manualName, setManualName] = useState('');
  const [manualTeam, setManualTeam] = useState('');
  const [manualPosition, setManualPosition] = useState('');
  
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // ========================================
  // SEARCH LOGIC
  // ========================================
  const performSearch = useCallback(async (query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    const trimmedQuery = query.trim();
    
    if (!trimmedQuery || trimmedQuery.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      setSearchError(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    
    try {
      const results = await leaguesAPI.searchMinorLeaguePlayers(leagueId, trimmedQuery, null, 15);
      
      if (results && Array.isArray(results) && results.length > 0) {
        setSearchResults(results);
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(true);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchError('Failed to search players');
      setSearchResults([]);
      setShowResults(true);
    } finally {
      setIsSearching(false);
    }
  }, [leagueId]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSearchError(null);
    setShowManualEntry(false);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    const trimmedQuery = query.trim();
    
    if (!trimmedQuery || trimmedQuery.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      setSearchError(null);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    setSearchError(null);
    setShowManualEntry(false);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setIsSearching(false);
    inputRef.current?.focus();
  };

  const handlePlayerClick = (player) => {
    setSearchQuery(player.info?.full_name || player.full_name || '');
    setShowResults(false);
    if (onPlayerSelect) {
      onPlayerSelect(player);
    }
  };

  const handleShowManualEntry = () => {
    setShowManualEntry(true);
    setShowResults(false);
    setManualName(searchQuery); // Pre-fill with search query
  };

  const handleManualSubmit = () => {
    if (!manualName || manualName.trim().length === 0) {
      setSearchError('Player name is required');
      return;
    }

    if (onMakePhantomPick) {
      onMakePhantomPick(manualName.trim(), manualTeam.trim() || null, manualPosition.trim() || null);
    }
  };

  const handleDraftPlayer = () => {
    if (selectedPlayer && onMakePick) {
      console.log('SELECTED PLAYER:', JSON.stringify(selectedPlayer, null, 2));
      const name = selectedPlayer.info?.full_name || selectedPlayer.full_name;
      const team = selectedPlayer.info?.mlb_team || selectedPlayer.mlb_team;
      const pos = selectedPlayer.info?.position || selectedPlayer.position;
      console.log('SENDING:', { name, team, position: pos });
      onMakePick(name, team || null, pos || null);
    }
  };

  // ========================================
  // CLICK OUTSIDE TO CLOSE
  // ========================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="space-y-4">
      {/* SEARCH INPUT */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={() => {
            const trimmedQuery = searchQuery.trim();
            if (trimmedQuery.length >= 2) {
              if (searchResults.length > 0 || searchError) {
                setShowResults(true);
              }
            }
          }}
          placeholder="Search for a player..."
          className={`w-full pl-10 pr-10 py-3 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} focus:${dynastyTheme.classes.border.primaryBright} focus:outline-none transition-colors`}
        />
        
        {isSearching ? (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className={`w-4 h-4 border-2 ${dynastyTheme.classes.border.primaryBright} border-t-transparent animate-spin rounded-full`} />
          </div>
        ) : searchQuery.length > 0 ? (
          <button
            onClick={handleClearSearch}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        ) : null}
      </div>

      {/* MIN CHARACTERS HELPER */}
      {searchQuery.length > 0 && searchQuery.trim().length < 2 && !isSearching && (
        <div className={`text-xs ${dynastyTheme.classes.text.neutralLighter}`}>
          Enter at least 2 characters to search
        </div>
      )}

      {/* SEARCH RESULTS DROPDOWN */}
      {showResults && (
        <div ref={dropdownRef} className={`${dynastyTheme.components.card.base} rounded-lg shadow-2xl border ${dynastyTheme.classes.border.neutral} overflow-hidden max-h-[400px] overflow-y-auto`}>
          {searchResults.length > 0 ? (
            <div className="py-2">
              {searchResults.map((player, index) => {
                const isDrafted = draftedPlayerIds.has(player.player_id);
                const ownedBy = player.owned_by_team_name;
                const isUnavailable = isDrafted || !!ownedBy;
                return (
                  <div
                    key={player.player_id || index}
                    onClick={() => !isUnavailable && handlePlayerClick(player)}
                    className={`px-4 py-3 flex items-center justify-between transition-all duration-150 ${
                      isUnavailable
                        ? 'opacity-50 cursor-not-allowed'
                        : `cursor-pointer hover:${dynastyTheme.classes.bg.darkLighter}`
                    }`}
                  >
                    <div>
                      <PlayerLink mlbId={player.player_id || null} name={player.info?.full_name || player.full_name} style={{ fontWeight: 600, color: isUnavailable ? '#6b7280' : '#f9fafb' }} />
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`text-sm ${dynastyTheme.classes.text.primary}`}>
                          {player.info?.position || player.position || 'UTIL'}
                        </span>
                        <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                          {player.info?.mlb_team || player.mlb_team || 'FA'}
                        </span>
                      </div>
                    </div>
                    {isDrafted ? (
                      <span className={`text-xs px-2 py-0.5 rounded ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.neutralDark}`}>
                        Drafted
                      </span>
                    ) : ownedBy ? (
                      <span className={`text-xs px-2 py-0.5 rounded ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.neutralDark}`}>
                        On {ownedBy}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={`p-4 text-center ${dynastyTheme.classes.text.neutralLight}`}>
              <User className={`w-8 h-8 mx-auto mb-2 ${dynastyTheme.classes.text.neutralDark}`} />
              <p>No players found matching "{searchQuery.trim()}"</p>
            </div>
          )}
          
          {/* MANUAL ENTRY BUTTON */}
          {searchQuery.trim().length >= 2 && !showManualEntry && (
            <div className={`px-4 py-3 border-t ${dynastyTheme.classes.border.neutral} ${dynastyTheme.classes.bg.dark}`}>
              <button
                onClick={handleShowManualEntry}
                className={`w-full ${dynastyTheme.utils.getComponent('button', 'secondary', 'sm')} justify-center`}
              >
                <Plus className="w-4 h-4 mr-2" />
                Player not found? Add manually
              </button>
            </div>
          )}
        </div>
      )}

      {/* ERROR MESSAGE */}
      {searchError && (
        <div className={`${dynastyTheme.components.badge.error} p-3 rounded-lg border`}>
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <p className="text-sm">{searchError}</p>
          </div>
        </div>
      )}

      {/* MANUAL ENTRY FORM */}
      {showManualEntry && (
        <div className={`${dynastyTheme.components.card.base} p-6 rounded-lg border-2 ${dynastyTheme.classes.border.primaryBright}`}>
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className={`w-6 h-6 ${dynastyTheme.classes.text.warning}`} />
            <h4 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
              Add Player Manually
            </h4>
          </div>
          
          <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/30">
            <p className="text-sm text-red-300 font-medium">
              This player was not found in any database. A phantom record will be created.
              Please verify the spelling is correct. The phantom will automatically resolve
              when the player appears in the MLB API.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                Player Name *
              </label>
              <input
                type="text"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                placeholder="e.g., Quinn Mathews"
                className={`w-full px-3 py-2 rounded ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} focus:${dynastyTheme.classes.border.primaryBright} focus:outline-none`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                MLB Team (Optional)
              </label>
              <input
                type="text"
                value={manualTeam}
                onChange={(e) => setManualTeam(e.target.value)}
                placeholder="e.g., STL, LAD"
                className={`w-full px-3 py-2 rounded ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} focus:${dynastyTheme.classes.border.primaryBright} focus:outline-none`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                Position (Optional)
              </label>
              <input
                type="text"
                value={manualPosition}
                onChange={(e) => setManualPosition(e.target.value)}
                placeholder="e.g., SP, OF, 1B"
                className={`w-full px-3 py-2 rounded ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} focus:${dynastyTheme.classes.border.primaryBright} focus:outline-none`}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleManualSubmit}
                disabled={isPickingPlayer || !manualName.trim()}
                className={`flex-1 ${dynastyTheme.utils.getComponent('button', 'primary', 'md')} justify-center`}
              >
                {isPickingPlayer ? 'Creating...' : 'Confirm — Create Player & Draft'}
              </button>
              <button
                onClick={() => {
                  setShowManualEntry(false);
                  setManualName('');
                  setManualTeam('');
                  setManualPosition('');
                }}
                disabled={isPickingPlayer}
                className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SELECTED PLAYER CONFIRMATION */}
      {selectedPlayer && !showManualEntry && (
        <div className={`${dynastyTheme.components.card.base} p-6 rounded-lg border-2 ${dynastyTheme.classes.border.primaryBright}`}>
          <h4 className={`text-lg font-bold ${dynastyTheme.classes.text.white} mb-4`}>
            Confirm Draft Pick
          </h4>
          
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
                <PlayerLink mlbId={selectedPlayer.player_id || null} name={selectedPlayer.info?.full_name || selectedPlayer.full_name} style={{ fontWeight: 700, fontSize: '1.5rem', color: '#f9fafb' }} />
              </div>
              <div className="flex items-center gap-3 mt-2">
                <span className={`${dynastyTheme.classes.text.primary} font-medium`}>
                  {selectedPlayer.info?.position || selectedPlayer.position || 'UTIL'}
                </span>
                <span className={dynastyTheme.classes.text.neutralLight}>
                  {selectedPlayer.info?.mlb_team || selectedPlayer.mlb_team || 'FA'}
                </span>
              </div>
            </div>
            <User className={`w-12 h-12 ${dynastyTheme.classes.text.neutralDark}`} />
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleDraftPlayer}
              disabled={isPickingPlayer}
              className={`flex-1 ${dynastyTheme.utils.getComponent('button', 'primary', 'md')} justify-center`}
            >
              {isPickingPlayer ? 'Drafting...' : 'Confirm Pick'}
            </button>
            <button
              onClick={() => {
                if (onPlayerSelect) onPlayerSelect(null);
                handleClearSearch();
              }}
              disabled={isPickingPlayer}
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerSearchWithManual;
